import { SelectChangeEvent } from '@mui/material';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useTransactionValidationGuard } from '../../../hooks/useTransactionValidationGuard';
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { AdjustPurchaseOrderRequestDto, BeginCreditReqRequest_LineItems, CalculateDiscrepanciesLine, GetConversionsResponseDto, Item, PurchaseOrderHeader, PutPurchaseOrderHeaderRequest, PutPurchaseOrderLineRequest, Uom, AddItemsInPurchaseOrderRequest, ReturnToVendorRequest, ReturnToVendorRequest_LineItems } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import { CreditRequestStatus, LineRecord } from '../interfaces/LineRecord';
import { ReceivedType } from '../enums/ReceivedType';
import { compare, exportFile } from '../../../utils';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import useLogError from '../../../hooks/useLogError';
import { UnitConversion } from '../interfaces/UnitConversion';
import { useTranslation } from 'react-i18next';
import { AdjustPurchaseOrderAlterationLine } from '../interfaces/AdjustOrders';
import { SelectableItem } from '../interfaces/SelectableItem';
import dayjs from 'dayjs';
import { alseaLACFranchises } from '../../../enums/FranchiseNames';
import { isReturnToVendorButtonVisibleForFranchise } from '../../../app/services/environment';
import { BatchRecord } from '../interfaces/BatchRecord';
import { client } from '../../../app/services/api/orderManagementClient';
import { useConfigurations } from '../../../components/shared/useConfigurations';

export const usePurchaseOrdersDetails = () => {
  const { addItemsToPurchaseOrder, getPurchaseOrder, hasOutStanding, purchaseordersPUT, calculatediscrepancies, getCreditRequestCutOff, beginCreditRequest, getConversions, adjustPurchaseOrder, getItems, getUoms, downloadPurchaseOrderById, getFranchiseOrderTypeStatus, allowNewReturnToVendorRequest, getLocalTime, postReturnToVendor, validateTransaction } = useNswagClient();
  const { getConfigurationValue, isConfigurationEnabled } = useConfigurations();
  const { runGuard, transactionValidationDialog } = useTransactionValidationGuard();
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const isClickable = isConfigurationEnabled(ConfigurationName.OneClickReceiveEnabled);
  const isAdjustEnabled = isConfigurationEnabled(ConfigurationName.PurchaseOrderAdjustmentsEnabled)
    && hasPermissionTo(['PurchaseOrderAdjustmentsWrite']);
  const canStoreAddItemsToPO = isConfigurationEnabled(ConfigurationName.AddItemsInPoEnabled)
    && hasPermissionTo(['PurchaseOrderAdjustmentsWrite']);
  const isReturnToVendorEnabled = isConfigurationEnabled(ConfigurationName.ReturnToVendorEnabled);
  const taxRateConfigurationValue = getConfigurationValue(ConfigurationName.TaxRatePercentage);
  const isTaxRateConfigEnabled = Boolean(taxRateConfigurationValue);
  const taxRate = Number(taxRateConfigurationValue ?? 0) / 100;
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderHeader | undefined>();
  const [search, setSearch] = useState('');
  const [poLineSearch, setPoLineSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedPoLineSearch, setAppliedPoLineSearch] = useState('');
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isStockCheckModalOpen, setIsStockCheckModalOpen] = useState(false);
  const [isCreditRequestModalOpen, setIsCreditRequestModalOpen] = useState(false);
  const [isCreditRequestCutOffModalOpen, setIsCreditRequestCutOffModalOpen] = useState(false);
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAdjustInstructionModal, setShowAdjustInstructionModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');
  const [note, setNote] = useState('');
  const [purchaseOrderNote, setPurchaseOrderNote] = useState('');
  const [lines, setLines] = useState<LineRecord[]>([]);
  const [noteId, setNoteId] = useState(-1);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof LineRecord>('itemNumber'); 
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [oneClickActive, setOneClickActive] = useState(false);
  const [discrepancies, setDiscrepancies] = useState<CalculateDiscrepanciesLine[]>();
  const [cutOffTime, setCutOffTime] = useState<string>('');
  const [uomConversionsCache, setUomConversionsCache] = useState<Map<string, UnitConversion[]>>(new Map<string, UnitConversion[]>());
  const [isAdjusting, setIsAdjusting] =  useState<boolean>(false);
  const [showAdjustButton, setShowAdjustButton] = useState<boolean>(false);
  const [canAdjust, setCanAdjust] = useState<boolean>(false);
  const [triggerButtonHeaders, setTriggerButtonHeaders] = useState<boolean>(false);
  const [originalLines, setOriginalLines] = useState<LineRecord[]>([]);
  const [showAutosaveMessage, setShowAutosaveMessage] = useState<boolean>(false);
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const [modifiedPOLines, setModifiedPOLines] = useState<Set<number>>(new Set());
  const totalCostPriceIncludingTax = useMemo(() => {
    if (!isTaxRateConfigEnabled) {
      return 0;
    }

    return purchaseOrders?.purchaseOrderLines?.reduce((total, line) => {
      if (line.unitCostPrice === undefined || line.unitCostPrice === null) {
        return total;
      }

      const lineCost = line.unitCostPrice * (line.expectedQuantity ?? 0);
      return total + lineCost + (lineCost * taxRate);
    }, 0) ?? 0;
  }, [isTaxRateConfigEnabled, purchaseOrders, taxRate]);
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);
  const [isCreatingReturnToVendor, setIsCreatingReturnToVendor] = useState<boolean>(false);
  const [disableUomLac, setDisableUomLac] = useState<boolean>(false);
  const [hasJustReceived, setHasJustReceived] = useState<boolean>(false);

  const [isOverReceivingDisabled, setIsOverReceivingDisabled] = useState<boolean>(false);

  const [ewsOrderNumber, setEwsOrderNumber ] = useState('');
  const [showOverReceivingWarningModal, setShowOverReceivingWarningModal] = useState<boolean>(false);
  const [cannotOverReceive, setCannotOverReceive] = useState<boolean>(false);
  const [cannotOverReceiveMap, setCannotOverReceiveMap] = useState<Map<number, boolean>>(new Map());
  const cannotOverReceiveRef = useRef<boolean>(false);
  const isCFADI = selectedStore?.franchiseName === 'Chick-fil-A Singapore' || selectedStore?.franchiseName === 'Chick-fil-A UK';

  const isAsc = order === 'asc';
  const { logError } = useLogError();
  const { t } = useTranslation('purchaseOrderDetails');
  const INACTIVITY_TIMEOUT = 60000; 

  useEffect(() => {
    cannotOverReceiveRef.current = cannotOverReceive;
  }, [cannotOverReceive]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    copyOrderedQuantities();
  }, [purchaseOrders]);
  
  
  useEffect(() => {
    showOneClickReciveButton();
    showAdjustVisibility();
    showReceiveButton();
    showReturnToVendorButton();
    allowEditTable();
    getAllowNewReturnToVendor();
    getIsReturnToVendorDateValid();
  }, [
    triggerButtonHeaders,
    isClickable,
    isAdjustEnabled,
    isReturnToVendorEnabled,
  ]);
    
  useEffect(() => {
    if (modifiedPOLines.size > 0 && !isReceiving && !cannotOverReceiveRef.current) {
      handleAutosaveMessage(false);
      startInactivityTimer();
    }
  }, [modifiedPOLines]);

  const startInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    const timer = setTimeout(() => {
      if (modifiedPOLines.size > 0 && !cannotOverReceiveRef.current) {
        modifiedPOLines.forEach(lineId => {
          autosaveItem(lineId);
        });
      }
    }, INACTIVITY_TIMEOUT); 
    setInactivityTimer(timer);
  };

  const blocker = useBlocker(() => modifiedPOLines.size > 0 && !cannotOverReceiveRef.current);

  useEffect(() => {
    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedPOLines.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedPOLines]);

  useEffect(() =>{
    if(lines?.length > 0){
      if(purchaseOrders?.isReceived && 
         purchaseOrders.creditRequestExpected && 
         !purchaseOrders.creditRequestStatus && 
         hasJustReceived){
        setHasJustReceived(false);

        if (!isCFADI) {
          checkDiscrepancies();
        }
      }
    }

  }, [lines]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/create-goods-receipt');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);
  
  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed(); 
    } };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset(); 
    } };

  const handleLeavePageModalConfirm = () => {
    saveClicked();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };



  const loadData = () => {
    setIsLoading(true);
    getPurchaseOrder(Number(id))
      .then((res) => {
        setPurchaseOrders(res.data);
        isPurchaseOrderDateValid(res.data?.deliveryDate ?? '');
        setPurchaseOrderNote(res.data?.note ?? '');
        setEwsOrderNumber(res.data?.ewsOrderNumber?.toString() ?? '');
        const franchiseName = selectedStore?.franchiseName ?? '';
        const isAlseaLACFranchises = alseaLACFranchises.includes(franchiseName ?? '');

        if(isAlseaLACFranchises){
          getOrderTypeOverReceivingValue(res.data?.orderType, franchiseName);
          setDisableUomLac(true);
        }
        const linesFromApi = res.data?.purchaseOrderLines?.map((poLineChange) => {
          const quantToUse = poLineChange.receivedQuantity == 0 && !poLineChange.receivedUom ? '' : String(poLineChange.receivedQuantity);
          
          const mappedBatches: BatchRecord[] = (poLineChange.batches ?? []).map(apiBatch => ({
            purchaseOrderLineId: poLineChange.id ?? -1,
            id: apiBatch.id,
            batchNumber: apiBatch.batchNumber ?? '',
            batchQuantity: apiBatch.batchQuantity ?? 0,
            originalBatchQuantity: apiBatch.originalBatchQuantity ?? 0,
            expirationDate: apiBatch.expirationDate ?? '',
          }));
          
          return {
            id: poLineChange.id ?? -1,
            quantity: quantToUse,
            uom: isAlseaLACFranchises ? poLineChange.expectedUom ?? '' : poLineChange.receivedUom ?? '',
            note: poLineChange.note ?? '',
            originalQuantity: quantToUse,
            originalUom: poLineChange.receivedUom ?? '',
            originalNote: poLineChange.note ?? '',
            expectedUom: poLineChange.expectedUom ?? '',
            expectedQuantity: poLineChange.expectedQuantity,
            uomList: poLineChange.uomList,
            itemNumber: poLineChange.itemNumber,
            itemDescription: poLineChange.itemDescription,
            lineNumber: poLineChange.lineNumber,
            tariffCode: poLineChange.tariffCode,
            hasDiscrepancy: poLineChange.hasDiscrepancy,
            originalReason: poLineChange.reasonForCreditRequest,
            reasonForCreditRequest: poLineChange.reasonForCreditRequest,
            batches: mappedBatches,
            receivedQuantity: poLineChange.receivedQuantity ?? 0,
            orderedQuantity: poLineChange.orderedQuantity ?? 0,
          };
        });
        setLines(linesFromApi ?? []);
        setModifiedPOLines(new Set());
        setOriginalLines(linesFromApi ?? []);
      }).finally(() => {
        setIsLoading(false);
        setTriggerButtonHeaders(!triggerButtonHeaders);
      });
  };

  const poLineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPoLineSearch(event.target.value);
  };

  const searchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleNoteButtonClick = (id: number, note: string) => {
    setNoteId(id);
    setNote(note);
    setIsNoteModalOpen(!isNoteModalOpen);
  };

  const closeModal = () => {
    setIsNoteModalOpen(false);
  };

  const closeConfirmModal = () => {
    setShowCopyModal(false);
    setIsConfirmOpen(false);
    setShowAdjustInstructionModal(false);
  };

  const closeAddItemModal = () => {
    setShowAddItemModal(false);
  };

  const confirmModal = () => {
    setIsConfirmOpen(false);
    const itemNumbers = lines.map(l => l.itemNumber ?? '').filter(Boolean);
    runGuard(
      () => validateTransaction(
        selectedStore?.storeNumber ?? '',
        purchaseOrders?.deliveryDate ?? new Date().toISOString(),
        itemNumbers,
      ),
      () => checkOutstandingStockcheck(),
    );
  };

  const acceptStockCountModal = () => {
    setIsStockCheckModalOpen(false);

    if(isAdjustEnabled && isAdjusting) 
    {
      adjustPurchaseOrders();
      setHasJustReceived(true);
    }
    else{
      receivePurchaseOrder();
      setHasJustReceived(true);
    }
  };

  const checkDiscrepancies = () => {
    const newDiscrepancies: CalculateDiscrepanciesLine[] = [];
    for (const item of lines) {
      if (item.quantity != item.expectedQuantity || item.uom !== item.expectedUom) {
        const uom = item.uom ?? item.expectedUom;
        const receivedQuantity = isNaN(Number(item.quantity)) ? 0 : Number(item.quantity);
        const lineItem: CalculateDiscrepanciesLine = {
          itemNumber: item.itemNumber,
          expectedQuantity: item.expectedQuantity,
          expectedUOM: item.expectedUom,
          receivedQuantity: receivedQuantity,
          receivedUOM: uom,
        };
        newDiscrepancies.push(lineItem); 
      } 
    }  
    setDiscrepancies(newDiscrepancies); 
    calculatediscrepancies(selectedStore?.storeNumber!, newDiscrepancies).then((result)=>{ 
      if (result?.length > 0) { 
        getCreditRequestCutOff(purchaseOrders?.ewsOrderNumber!, selectedStore?.storeNumber).then((result)=>{
          const dateToCompare = new Date(result);  
          if (dateToCompare >= new Date()) {
            raiseCreditRequest();
          }
          else
          {
            setCutOffTime(result);
            setIsCreditRequestCutOffModalOpen(true);
          }
        }); 
      }
    }).catch((error) => {
      logError(error);
    });
  }; 

  const raiseCreditRequest = () => {
    setIsCreditRequestModalOpen(true);
  };

  const closeStockCountModal = () => {
    setIsStockCheckModalOpen(false);
  };

  const closeCreditRequestModal = () => {
    setIsCreditRequestModalOpen(false);
  };

  const createCreditRequestFromModal = () => {
    setIsCreditRequestModalOpen(false);
    const beginCreditReqRequestLineItems: BeginCreditReqRequest_LineItems[] = discrepancies!.map(line => ({
      itemNumber: line.itemNumber ?? '',
      receivedQuantity: line.receivedQuantity ?? 0,
      receivedUOM: line.receivedUOM ?? '',
    }));
    const creditRequest = {
      ewsOrderNumber: purchaseOrders?.ewsOrderNumber!,
      items:beginCreditReqRequestLineItems,
      storeNumber: selectedStore?.storeNumber!,
    };
    beginCreditRequest(creditRequest).then(() =>
    {
      purchaseOrders && navigate(`/store/credit-request/${purchaseOrders.id}`);
    });

  };

  const closeCreditRequestCutOffModal = () => {
    setIsCreditRequestCutOffModalOpen(false);
  };

  const isEdited = (line: LineRecord | undefined) => {
    if (!line) return false;
    return modifiedPOLines.has(line.id) && (line.originalQuantity != line.quantity || line.originalUom != line.uom || line.originalNote != line.note);
  };  
  
  const adjustBatchQuantities = (line: LineRecord, newQuantity: number): BatchRecord[] => {


    if (!line.batches || line.batches.length === 0) {
      return [];
    }


    if (newQuantity === 0) {
      return line.batches.map(batch => ({
        ...batch,
        batchQuantity: 0,
      }));
    }

    const originalQuantity = Number(line.originalQuantity) || 0;
    const totalOriginalBatchQuantity = line.batches.reduce((total, batch) => total + batch.originalBatchQuantity, 0);

    if (newQuantity === originalQuantity && originalQuantity > 0) {
      return line.batches.map(batch => ({
        ...batch,
        batchQuantity: batch.originalBatchQuantity,
      }));
    }


    if (newQuantity > totalOriginalBatchQuantity) {

      const updatedBatches = line.batches.map(batch => ({
        ...batch,
        batchQuantity: batch.originalBatchQuantity,
      }));

      const extraQuantity = newQuantity - totalOriginalBatchQuantity;

      const batchesWithDates = updatedBatches.filter(batch =>
        batch.expirationDate && batch.expirationDate.trim() !== '',
      );

      if (batchesWithDates.length > 0) {

        const sortedBatches = batchesWithDates.sort((a, b) =>
          b.expirationDate.localeCompare(a.expirationDate),
        );
        const latestBatch = sortedBatches[0];


        const batchIndex = updatedBatches.findIndex(batch =>
          batch.id === latestBatch.id ||
          (batch.tempId && batch.tempId === latestBatch.tempId),
        );

        if (batchIndex !== -1) {
          updatedBatches[batchIndex] = {
            ...updatedBatches[batchIndex],
            batchQuantity: updatedBatches[batchIndex].batchQuantity + extraQuantity,
          };
        }
      } else {

        const lastBatchIndex = updatedBatches.length - 1;
        updatedBatches[lastBatchIndex] = {
          ...updatedBatches[lastBatchIndex],
          batchQuantity: updatedBatches[lastBatchIndex].batchQuantity + extraQuantity,
        };
      }

      return updatedBatches;
    }


    const updatedBatches = line.batches.map(batch => ({
      ...batch,
      batchQuantity: batch.originalBatchQuantity,
    }));

    let remainingToRemove = totalOriginalBatchQuantity - newQuantity;


    const batchesWithDates = updatedBatches.filter(batch =>
      batch.expirationDate && batch.expirationDate.trim() !== '',
    );

    if (batchesWithDates.length > 0) {

      const sortedBatches = batchesWithDates.sort((a, b) =>
        a.expirationDate.localeCompare(b.expirationDate),
      );


      for (const sortedBatch of sortedBatches) {
        if (remainingToRemove <= 0) break;

        const batchIndex = updatedBatches.findIndex(batch =>
          batch.id === sortedBatch.id ||
          (batch.tempId && batch.tempId === sortedBatch.tempId),
        );

        if (batchIndex !== -1) {
          const currentQuantity = updatedBatches[batchIndex].batchQuantity;
          const removeAmount = Math.min(remainingToRemove, currentQuantity);

          updatedBatches[batchIndex] = {
            ...updatedBatches[batchIndex],
            batchQuantity: Math.max(0, currentQuantity - removeAmount),
          };

          remainingToRemove -= removeAmount;
        }
      }
    } else {

      const lastBatchIndex = updatedBatches.length - 1;
      const currentQuantity = updatedBatches[lastBatchIndex].batchQuantity;
      const removeAmount = Math.min(remainingToRemove, currentQuantity);

      updatedBatches[lastBatchIndex] = {
        ...updatedBatches[lastBatchIndex],
        batchQuantity: Math.max(0, currentQuantity - removeAmount),
      };
    }

    return updatedBatches;
  };

  const handleQuantityChange = (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLines = lines.map(a => {
      const returnValue = { ...a };
      if (a.id == id) {
        const convertedQty = Number(event.target.value);
        const actualQuantity = isNaN(convertedQty) ? 0 : Math.max(0, convertedQty);
        returnValue.quantity = actualQuantity.toString();
        if (returnValue.batches && returnValue.batches.length > 0) {
          returnValue.batches = adjustBatchQuantities(returnValue, actualQuantity);
        }
      }
      return returnValue;
    });
    setLines(newLines);
    const poLine = newLines.find(x => x.id == id);
    if (poLine) updateHasDiscrepancy(poLine);
    if (poLine?.id) {
      setModifiedPOLines(prev => { 
        const updatedSet = new Set(prev);
        if (!updatedSet.has(poLine.id)) {
          updatedSet.add(poLine.id);
        }
        return updatedSet;
      });
    }

  };
 
  const handlePOLineFocus = (itemId: number) => {
    if (!cannotOverReceiveRef.current && lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedPOLines.has(lastFocusedItemId)) {
      autosaveItem(lastFocusedItemId);
    }
    setLastFocusedItemId(itemId);
  };

  const autosaveItem = (itemId: number) => {
  
    if (itemId) {
      if(isAdjustEnabled && isAdjusting) 
      {
        //adjustPurchaseOrders();
      }else {
        save(lines, false, true);
      }
      
      setModifiedPOLines(prev => {
        const newModifiedPOLines = new Set(prev);
        newModifiedPOLines.delete(itemId);
        return newModifiedPOLines;
      });
    }
    else {
      handleAutosaveMessage(false);
    }
  };
  const handleUomsChange = (id: number) => (event: SelectChangeEvent) => {
    const resetUomList = purchaseOrders?.purchaseOrderLines?.find(poLine => poLine.id === id)?.uomList;

    const newLines = lines.map(a => {
      const returnValue = { ...a };
      if (a.id == id) {
        returnValue.uom = event.target.value;
        returnValue.uomList = resetUomList;
      }
      return returnValue;
    });

    setLines(newLines);
    
    const poLine = newLines.find(x => x.id == id);
    if (poLine) updateHasDiscrepancy(poLine);
    if (poLine?.id) {
      setModifiedPOLines(prev => {
        const updatedSet = new Set(prev);
        if (!updatedSet.has(poLine.id)) {
          updatedSet.add(poLine.id);
        }
        return updatedSet;
      });
    }
  };
  const closeOverReceivingWarning = () => {
    setShowOverReceivingWarningModal(false);
  };

  const handleReasonChange = (id: number, reason: string) => {
    const newLines = lines.map(a => {
      const returnValue = { ...a };
      if (a.id == id) {
        returnValue.reasonForCreditRequest = reason;
      }
      return returnValue;
    });
    setLines(newLines);
  };

  const handleNotesChange = (id: number, note: string) => {
    const newLines = lines.map(a => {
      const returnValue = { ...a };
      if (a.id == id) {
        returnValue.note = note;
      }
      return returnValue;
    });
    setLines(newLines);
  };

  const searchClick = () => {
    setAppliedPoLineSearch(poLineSearch);
    setAppliedSearch(search);
  };

  const filteredPoLines = lines?.filter(x => x.lineNumber?.includes(appliedPoLineSearch) || appliedPoLineSearch === '')
    .filter(x => x.itemNumber?.toLocaleLowerCase().includes(appliedSearch.toLocaleLowerCase()) || x.itemDescription?.toLocaleLowerCase().includes(appliedSearch.toLocaleLowerCase()) || search === '');

  const sortedPoLines = filteredPoLines?.sort((a, b) => compare(a, b, orderBy, order));

  const isShowingTrafficCode = purchaseOrders?.purchaseOrderLines?.some(x => x.tariffCode);

  const handlePurchaseOrderNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPurchaseOrderNote(event.target.value);
  };

  const showCopyQuantityModal=()=> {
    if (isReceiving) {
      return;
    }
    setShowCopyModal(true);
  };

  const copyOrderQuantities = () => {
    setShowCopyModal(false);
    const updatedLines = lines.map(line => {
      const quantToUse = line.expectedQuantity == 0 ? '' : String(line.expectedQuantity);

      let newUomList = line.uomList;
      if (!line.uomList?.some(uom => uom.code === line.expectedUom)) {
        newUomList = line.uomList?.concat([{ code: line.expectedUom }]);
      }

      return { ...line, quantity: quantToUse, uom: line.expectedUom, isEdited: true, uomList: newUomList };
    });
    setLines(updatedLines);
    return updatedLines;
  };

  const copyOrderedQuantities = () => {
    if (purchaseOrders?.isReceived) return;

    const updatedLines = lines.map(line => {
      let quantToUse = line.quantity;
      let expectedQuantity = line.expectedQuantity;

      if (!line.uom) {
        quantToUse = purchaseOrders?.erpOrderNumber
          ? line.expectedQuantity?.toString()
          : line.orderedQuantity?.toString() ?? '';
      }

      expectedQuantity = purchaseOrders?.erpOrderNumber
        ? expectedQuantity
        : line.orderedQuantity;

      const newUomList = line.uomList?.some(uom => uom.code === line.expectedUom)
        ? [...line.uomList]
        : [...(line.uomList ?? []), { code: line.expectedUom }];

      const uomToUse = line.uom || line.expectedUom;

      return {
        ...line,
        quantity: quantToUse,
        expectedQuantity,
        uom: uomToUse,
        isEdited: true,
        uomList: newUomList,
      };
    });

    setLines(updatedLines);
    return updatedLines;
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };
  const saveClicked = () => {
    if(isAdjustEnabled && isAdjusting) {
      const itemNumbers = lines.map(l => l.itemNumber ?? '').filter(Boolean);
      runGuard(
        () => validateTransaction(
          selectedStore?.storeNumber ?? '',
          purchaseOrders?.deliveryDate ?? new Date().toISOString(),
          itemNumbers,
        ),
        () => {
          adjustPurchaseOrders();
          setHasJustReceived(true);
        },
      );
    }
    else{
      save(lines, false);
    }
  };

  const addItemClicked = () => { 
    setShowAddItemModal(true);
  };

  const save = (linesToSave: LineRecord[], isReceived: boolean = false, isAutosaving = false) => {
    const someLinesFilled = linesToSave.some(line => line.uom != '');

    if (someLinesFilled) {
      const poLines: PutPurchaseOrderLineRequest[] = linesToSave.filter(x => x.uom != '').map(line => {
        return {
          id: line.id,
          receivedUom: line.uom,
          receivedQuantity: Number(line.quantity),
          note: line.note,
          isSelected: isEdited(line),
          creditedAmount: 0,
          expectedQuantity: line.expectedQuantity,
          reason: line.reasonForCreditRequest,
          expectedUom: line.expectedUom,
          itemNumber: line.itemNumber,
          batches: line.batches?.map(batch => ({
            id: batch.id,
            batchNumber: batch.batchNumber,
            batchQuantity: batch.batchQuantity,
            originalBatchQuantity: batch.originalBatchQuantity,
            expirationDate: batch.expirationDate,
          })) ?? [],
        };
      });

      const body: PutPurchaseOrderHeaderRequest = {
        packingSlipNumber: purchaseOrders?.packingSlipNumber,
        acceptedDate: new Date().toISOString(),
        note: purchaseOrderNote,
        isReceived: isReceived,
        receivedType: oneClickActive ? ReceivedType.OneClick : ReceivedType.Standard,
        putPurchaseOrderLineRequests: poLines,
      };
      if(!isAutosaving){
        setSnackBarProps('info', t('saving'));
        setIsProcessing(true); 
      }  
      purchaseordersPUT(purchaseOrders?.id ?? -1, body)
        .then(() => {
          if(isAutosaving){      
            handleAutosaveMessage(true);
          }
          else{
            setSnackBarProps('success', t('successSavingPO'));
            loadData();
          }
        })
        .catch((error) => {
          setSnackBarProps('error', t('unableSavingPO') + ' ' + error);
        })
        .finally(() => {
          setIsProcessing(false);
          setIsConfirmOpen(false);
        });
    }
    else {
      setSnackBarProps('warning', t('warningSavingPO'));
    }
  };

  const handleAutosaveMessage = (changesSaved : boolean) => {
    setShowAutosaveMessage(changesSaved);
  };

  const adjustPurchaseOrders = ()=> {
    if(!purchaseOrders?.isReceived){
      logError('Cannot adjust goods receipt which has not already been received');
      return;
    }
    const poLines: AdjustPurchaseOrderAlterationLine[] = [];
    const changedLine = lines.filter(x=> (x.quantity !== x.originalQuantity) || (x.uom !== x.originalUom) || (x.note !== x.originalNote) || (x.reasonForCreditRequest !== x.originalReason));
    for(const line of changedLine){
      if(!line.quantity || parseFloat(line.quantity)< 0 || !line.uom){
        setSnackBarProps('error', t('invalidData'));
      }else{
        const originalLine = purchaseOrders.purchaseOrderLines?.find(l => l.id == line.id);
        poLines.push({
          itemNumber: line.itemNumber,
          oldQuantity: originalLine?.receivedQuantity!,
          newQuantity: parseInt(line.quantity),
          oldQuantityUom: originalLine?.receivedUom!,
          newQuantityUom: line.uom,
          note: line.note,
          isSelected: false,
          sequenceNumber: 0,
          reasonForCreditRequest: line.reasonForCreditRequest,
        });
      }
    }
    if(poLines.length > 0){

      const purchaseOrderDataToSave: AdjustPurchaseOrderRequestDto = {
        alterations: poLines,
        receivedType: ReceivedType.Standard,
      };
      adjustPOR(purchaseOrderDataToSave);
    }
    else{
      setSnackBarProps('error', t('noLineEdited'));
      logError('There are no edited line');
    }
  };
  
  const adjustPOR = (purchaseOrderDataToSave: AdjustPurchaseOrderRequestDto) => {
    setSnackBarProps('info',t('saving'));
    setIsProcessing(true);   
    adjustPurchaseOrder(purchaseOrders?.id ?? -1, purchaseOrderDataToSave)
      .then(() => {
        setSnackBarProps('success', t('successSavingPO'));
        loadData();
      })
      .catch((error) => {
        setSnackBarProps('error', t('unableSavingPO') + ' ' + error);
      })
      .finally(() => {
        setIsProcessing(false);
        setIsAdjusting(false);
        setIsConfirmOpen(false);
      });
  };

  const downloadClicked = () => {
    setIsDownloading(true);
    downloadPurchaseOrderById(Number(id))
      .then((data) => {
        exportFile(data.data,data.headers?.['content-type'], data.fileName ?? '');
      })
      .catch((error) => {
        setSnackBarProps('error', t('downloadFailed') + ' ' + error);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };
  const receivePurchaseOrder = () => {
    let linesToSave = lines;

    if (oneClickActive) {
      linesToSave = copyOrderQuantities();
    }

    save(linesToSave, true);
  };
  const receiveClick = () => {
    setOneClickActive(false);
    const allLinesFilled = lines.every(line => line.uom != '');
    const allLinesHaveValue = lines.every(line => line.quantity != '');
    if (allLinesFilled && allLinesHaveValue) {
      setIsConfirmOpen(true);
    }
    if(!allLinesFilled) {
      setSnackBarProps('warning', t('warningUOMFilled'));
    }
    if(!allLinesHaveValue) {
      setSnackBarProps('warning', t('warningQTYFilled'));
    }
  };

  const returnToVendorClick = () => {
    setIsCreatingReturnToVendor(true);
    lines?.forEach((line) => {
      line.quantity = '0';
    });
  };
  const returnToVendorCancelClick = () => {
    setIsCreatingReturnToVendor(false);
    lines?.forEach((line) => {
      line.quantity = line.originalQuantity;
      line.uom = line.originalUom;
    });
  };

  const oneClickReceiveClick = () => {
    setOneClickActive(true);
    const itemNumbers = lines.map(l => l.itemNumber ?? '').filter(Boolean);
    runGuard(
      () => validateTransaction(
        selectedStore?.storeNumber ?? '',
        purchaseOrders?.deliveryDate ?? new Date().toISOString(),
        itemNumbers,
      ),
      () => checkOutstandingStockcheck(),
    );
  };

  const checkOutstandingStockcheck = () => {
    hasOutStanding(selectedStore?.storeNumber).then((result) => {
      if (result.data) {
        // If there are outstanding stock checks, show the stock check modal
        setIsStockCheckModalOpen(true);
      } else {
        // If there are no outstanding stock checks, mark the purchase order as received
        acceptStockCountModal();
      }
    })
      .catch((error) => {
        logError(error);
      });

  };
  
  const isReceiving = purchaseOrders?.isReceived ?? true;
  
  const [showReceive, setShowReceive] = useState<boolean>();
  const [showOneClickReceive, setShowOneClickReceive] = useState<boolean>();
  const [isEditable, setIsEditable] = useState<boolean>();
  const [showReturnToVendor, setShowReturnToVendor] = useState<boolean>();
  const [isReturnToVendorPendingStatus, setIsReturnToVendorPendingStatus] = useState<boolean>();
  const [isReturnToVendorDateValid, setIsReturnToVendorDateValid] = useState<boolean>();
  const [rmaErrorMessage, setRmaErrorMessage] = useState<string>();
  
  const allowEditTable = () => {
    // When return to vendor part is implemented one more case for that button should be added here
    // add this or case ( || this.isCreatingReturnToVendor)
    if( purchaseOrders && (!purchaseOrders?.isReceived || isAdjusting)){
      setIsEditable(true);
    }else{
      setIsEditable(false);
    }
  };

  const showReceiveButton =() => {
    const canEdit  =  hasPermissionTo(['PurchaseOrderWrite']);
    if(canEdit && purchaseOrders && (!purchaseOrders?.isReceived || isAdjusting)){
      setShowReceive(true);
    }
    else{
      setShowReceive(false);
    }
  };

  const showReturnToVendorButton =() => {
    setShowReturnToVendor(
      isReturnToVendorEnabled
      && isReturnToVendorButtonVisibleForFranchise(selectedStore?.franchiseName ?? ''),
    );
  };

  const getAllowNewReturnToVendor = () => {
    if(purchaseOrders?.id){
      allowNewReturnToVendorRequest(purchaseOrders?.id)
        .then((result) => {
          setIsReturnToVendorPendingStatus(result);
        })
        .catch((e) => logError(e));
    }
  };

  const getIsReturnToVendorDateValid = () => {
    if(purchaseOrders?.id){
      getLocalTime(purchaseOrders?.id)
        .then((result) => {
          if (showAdjustButton || isAdjusting) {
            setIsReturnToVendorDateValid(false);
            return;
          }

          const creditRequestIsRaised =
          purchaseOrders?.creditRequestStatus === CreditRequestStatus.SENT ||
          purchaseOrders?.creditRequestStatus === CreditRequestStatus.RECEIVED;

          let sentDateTime = result.data ?? purchaseOrders?.acceptedDate ?? '';
          let threshold = 720; //30 days

          if (creditRequestIsRaised) {
            sentDateTime = purchaseOrders?.creditRequestSentDateTime ?? '';
            threshold = 48;
          }

          let diff = dayjs().diff(dayjs(sentDateTime), 'hour');

          if (diff > threshold) {
            sentDateTime = result.data ?? purchaseOrders?.acceptedDate ?? '';
            threshold = 720;
            diff = dayjs().diff(dayjs(sentDateTime), 'hour');
          }

          setIsReturnToVendorDateValid(diff <= threshold);
        }).catch((e) => logError(e));
    }
  };

  const submitReturnToVendor = (rmaNumber: string) => {
    const items = lines?.filter(x => x.uom !== '').map((x) => {
      const quantity = x.quantity ? +x.quantity : 0;
      const poLine: ReturnToVendorRequest_LineItems = {
        itemNumber: x.itemNumber ?? '',
        returnToVendorQuantity: quantity,
        returnToVendorUom: disableUomLac ? x.expectedUom ?? '' : x.uom ?? '',
      };

      return poLine;
    });

    const request: ReturnToVendorRequest = {
      customerRmaNumber: rmaNumber,
      purchaseOrderHeaderId: purchaseOrders?.id?.toString() ?? '',
      storeNumber: purchaseOrders?.storeNumber ?? '',
      items: items ?? [],
    };

    const doReturnToVendor = () => {
      postReturnToVendor(request)
        .then((result) => {
          navigate('/store/credit-request/' + result.data?.purchaseOrderHeaderId);
        })
        .catch((e) => {
          logError(e);
          setRmaErrorMessage(e);
        });
    };

    const itemNumbers = lines.map(l => l.itemNumber ?? '').filter(Boolean);
    runGuard(
      () => validateTransaction(
        selectedStore?.storeNumber ?? '',
        new Date().toISOString(),
        itemNumbers,
      ),
      doReturnToVendor,
    );
  };
  
  const showOneClickReciveButton =() => {
    const canEdit  =  hasPermissionTo(['PurchaseOrderWrite']);
    if(canEdit && isClickable && purchaseOrders && !purchaseOrders?.isReceived){
      setShowOneClickReceive(true);
    }
    else{
      setShowOneClickReceive(false);
    }
    if(canEdit  && purchaseOrders && !purchaseOrders?.isReceived){
      setShowSave(true);
    }
    else{
      setShowSave(false);
    }
  };
  
  const handleSort = (orderBy: keyof LineRecord) => {
    const order = isAsc ? 'desc' : 'asc';
    setOrder(order);
    setOrderBy(orderBy);
  };

  const clearAll = () => {
    setSearch('');
    setPoLineSearch('');
    setAppliedPoLineSearch('');
    setAppliedSearch('');
  };

  const updateHasDiscrepancy = (poLine: LineRecord) => {
    if(!uomConversionsCache.has(poLine.itemNumber ?? '')) {
      setUOMConversions(poLine);
    } else {
      checkForDiscrepancy(poLine, undefined);
    }
  };

  const setUOMConversions = (poLine: LineRecord) => {
    getConversions(poLine.itemNumber, undefined, undefined, undefined, selectedStore?.storeNumber)
      .then((result: GetConversionsResponseDto) => {
        if(result?.conversions) {
          const newCache = new Map(uomConversionsCache);
          newCache.set(poLine.itemNumber ?? '', result.conversions as UnitConversion[]);
          setUomConversionsCache(newCache);
          checkForDiscrepancy(poLine, newCache);
        }
      }).catch((error) => {
        logError(error);
      });
  };

  const checkForDiscrepancy = (poLine: LineRecord, uomConversionsCacheParam: Map<string, UnitConversion[]> | undefined) => {
    setLines((prevValues) => {
      const index = prevValues.findIndex(x => x.id === poLine.id);

      if (index < 0) return prevValues;

      const item = prevValues[index];
      item.hasDiscrepancy = calculateDiscrepancy(item, uomConversionsCacheParam);
      return [...prevValues];
    });
  };

  const handleOverReceiving = (overReceiving: boolean, lineId: number) => {
    setCannotOverReceiveMap((prev) => {
      const updatedMap = new Map(prev);
      updatedMap.set(lineId, overReceiving);
      const anyOverReceiving = Array.from(updatedMap.values()).some(value => value);
      setCannotOverReceive(anyOverReceiving);
      return updatedMap;
    });
    handleAutosaveMessage(false);

    setShowOverReceivingWarningModal(overReceiving);
  };

  const calculateDiscrepancy = (line: LineRecord, uomConversionsCacheParam: Map<string, UnitConversion[]> | undefined) => {
    const { quantity = 0, expectedQuantity = 0, uom, expectedUom, itemNumber } = line;

    const actualQuantity = isNaN(Number(quantity)) ? 0 : Number(quantity);
    if (uom === expectedUom) {

      const isOverReceiving = actualQuantity > expectedQuantity;
      handleOverReceiving(isOverReceivingDisabled && isOverReceiving, line.id);

      return actualQuantity < expectedQuantity;
    } 
    else {
      const uomConversionsCacheTemp = uomConversionsCacheParam ?? uomConversionsCache;
      const conversion = uomConversionsCacheTemp.get(itemNumber ?? '')?.find(x => x.fromUom === uom && x.toUom === expectedUom);
      if (conversion) {

        const convertedExpectedQuantity = expectedQuantity * (conversion.conversionNumber ?? 0);

        const isOverReceiving = actualQuantity > convertedExpectedQuantity;
        handleOverReceiving(isOverReceivingDisabled && isOverReceiving, line.id);
        
        return actualQuantity < convertedExpectedQuantity ;
      } else {
        handleOverReceiving(false, line.id);
        return false;
      }
    }
  };

  const isPurchaseOrderDateValid = (incomingDate: string | undefined) => {
    const deliveryDate = new Date(incomingDate ?? '').setHours(11, 0, 0, 0);

    const differenceDelivery = getDifferenceBetweenDatesInHours(deliveryDate, new Date());
    if (isCFADI) {
      setCanAdjust(differenceDelivery < 25);
    }
    else{
      setCanAdjust(differenceDelivery < 45);
    }
  };

  const showAdjustVisibility = () => {
    if (
      isAdjustEnabled &&
    purchaseOrders &&
    isReceiving &&
    !(purchaseOrders.creditRequestStatus === CreditRequestStatus.RECEIVED || purchaseOrders.creditRequestStatus === CreditRequestStatus.SENT) &&
    (isCFADI || canAdjust) &&
    !isAdjusting
    ) {
      setShowAdjustButton(true);
    } else {
      setShowAdjustButton(false);
    }
  };


  const adjustClicked =() =>{
    if (isCFADI && !isAdjusting && !canAdjust) {
      setShowAdjustInstructionModal(true);
      return;
    }
    setIsAdjusting(true);
    setTriggerButtonHeaders(!triggerButtonHeaders);
  };

  const cancelAdjust =() =>{
    setIsAdjusting(false);
    setLines(originalLines ?? []);
    setTriggerButtonHeaders(!triggerButtonHeaders);
  };


  const getOrderTypeOverReceivingValue =(orderType : string | undefined, franchiseName : string | undefined) => {
    getFranchiseOrderTypeStatus(franchiseName, orderType)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((result: any) => {
        setIsOverReceivingDisabled(result.data.overReceivingDisabled);
      })
      .catch((error) => {
        logError(error);
      });
  };


  const getDifferenceBetweenDatesInHours = (startDate: number, endDate:Date) => {
    const start = new Date(startDate).getTime(); 
    const end = new Date(endDate).getTime();
    const differenceMs = Math.abs(end - start);
    return Math.floor(differenceMs / (1000 * 60 * 60));
  };

  const [isLoadingItemData, setIsLoadingItemData] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectableItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSearchTermChange = useCallback(async (searchTerm: string) => {

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchTerm(searchTerm);

    if (searchTerm.trim() === '') {
      setIsLoadingItemData(false);
      setItems([]);
      return;
    }

    if (searchTerm.trim().length < 2) {
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsLoadingItemData(true);
        setItems([]);
        const response = await getItems(
          searchTerm,
          selectedStore?.storeNumber,
          undefined,
          false,
          true,
          true,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          0,
          10,
          true,
        );
        if (response?.data?.items) {
          setItems(response.data.items);
        }
      } catch (error) {
        logError(error);
      } finally {
        setIsLoadingItemData(false);
      }
    }, 500);
  }, [selectedStore?.storeNumber]);

  const possibleUOMs = async (storeNumber: string, itemNumber: string): Promise<Uom[]> => {
    let uomReturn: Uom[] = [];
    await getUoms(itemNumber, storeNumber, true, true)
      .then((results) => {
        const uoms = results.data?.uoms;
        uomReturn = uoms ?? [];
      })
      .catch((error) => logError(error));
    return uomReturn;
  };

  const itemSelected = async (items: Item[]) => {
    // Filter items that are not already in the selectedItems array
    const itemsNotIncluded: SelectableItem[] = await Promise.all(
      items
        .filter(item => !selectedItems.some(selectedItem => selectedItem.id === item.id))
        .map(async item => {
          const possibleUoms = await possibleUOMs(selectedStore?.storeNumber ?? '', item.itemNumber ?? '-1');
          return {
            ...item,
            possibleUoms,
            quantity: 0,
          };
        }),
    );
  
    // Keep items that are already in the selectedItems array and present in the new selection
    const itemsToKeep = selectedItems.filter(item => items.some(i => i.id === item.id));
  
    // Update selectedItems state with the combined array of kept and newly added items
    setSelectedItems([...itemsToKeep, ...itemsNotIncluded]);
  
    setSearchTerm('');
  };

  const handleUomChange = (id: number, value: string) =>{
    setSelectedItems(prevSelectedItems =>
      prevSelectedItems.map(item => {
        if (item.id !== id) {
          return item;
        }

        const currentRate = item.possibleUoms?.find(uom => uom.code === item.uom)?.conversionRate;
        const newRate = item.possibleUoms?.find(uom => uom.code === value)?.conversionRate;
        // no conversion path for this UOM pair: clear the stale price instead of keeping one priced for the old UOM
        const rescaledCostPrice = (currentRate && newRate)
          ? Math.round((item.costPrice ?? 0) * currentRate / newRate * 10000) / 10000
          : undefined;

        return { ...item, uom: value, costPrice: rescaledCostPrice };
      }),
    );
  };

  const handleModalQuantityChange = (id: number, value: string) =>{
    const convertedQty = Number(value);
    const actualQuantity = isNaN(convertedQty) ? 0 : Math.max(0, convertedQty);

    setSelectedItems(prevSelectedItems =>
      prevSelectedItems.map(item =>
        item.id === id ? { ...item, quantity: actualQuantity } : item,
      ),
    );
  };

  const handleCostPriceChange = (id: number, value: string) =>{
    const convertedPrice = Number(value);
    const actualPrice = isNaN(convertedPrice) ? 0 : Math.max(0, convertedPrice);

    setSelectedItems(prevSelectedItems =>
      prevSelectedItems.map(item =>
        item.id === id ? { ...item, costPrice: actualPrice } : item,
      ),
    );
  };
  
  const confirmAddItems = () =>{
    const request: AddItemsInPurchaseOrderRequest = {
      purchaseOrderHeaderId: purchaseOrders?.id ?? -1,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: selectedItems.map(item => ({
        itemNumber: item.itemNumber ?? '',
        description: item.description ?? '',
        requestedQuantity: item.quantity ?? -1,
        uom: item.uom ?? '',
        costPrice: item.costPrice ?? -1,
      })),
    };

    addItemsToPurchaseOrder(request)
      .then(() => {
        closeAddItemModal();
        setSelectedItems([]);
        loadData();
      })
      .catch((e) => {       
        setSnackBarProps('error', t('unableToAddItemToPO') + ' ' + e);
      });
  };

  return {
    transactionValidationDialog,
    downloadClicked,
    poLineChange,
    searchChange,
    handleNoteButtonClick,
    closeModal,
    closeStockCountModal,
    closeCreditRequestModal,
    createCreditRequestFromModal,
    closeCreditRequestCutOffModal,
    handleQuantityChange,
    handleUomsChange,
    handleReasonChange,
    handleNotesChange,
    sortedPoLines,
    isShowingTrafficCode,
    handlePurchaseOrderNoteChange,
    copyOrderQuantities,
    closeSnackBar,
    saveClicked,
    receiveClick,
    oneClickReceiveClick,
    returnToVendorClick,
    returnToVendorCancelClick,
    showReturnToVendor,
    isReturnToVendorPendingStatus,
    isReturnToVendorDateValid,
    isCreatingReturnToVendor,
    submitReturnToVendor,
    rmaErrorMessage,
    setRmaErrorMessage,
    isReceiving,
    id,
    ewsOrderNumber,
    purchaseOrders,
    isStockCheckModalOpen,
    isCreditRequestModalOpen,
    isCreditRequestCutOffModalOpen,
    snackBarDuration,
    isSnackBarOpen,
    snackbarMessage,
    snackBarSeverity,
    note,
    noteId,
    purchaseOrderNote,
    search,
    poLineSearch,
    cutOffTime,
    isEdited,
    lines,
    isNoteModalOpen,
    order,
    orderBy,
    handleSort,
    isConfirmOpen,
    closeConfirmModal,
    acceptStockCountModal,
    checkDiscrepancies,
    isLoading,
    receivePurchaseOrder,
    isProcessing,
    searchClick,
    clearAll,
    canAdjust,
    cannotOverReceive,
    showOverReceivingWarningModal,
    showCopyModal,
    showAdjustInstructionModal,
    showCopyQuantityModal,
    showAdjustButton,
    showReceive,
    adjustClicked,
    isEditable,
    confirmModal,
    isClickable,
    isAdjusting,
    cancelAdjust,
    showOneClickReceive,
    showSave,
    addItemClicked,
    showAddItemModal,
    handleSearchTermChange,
    items,
    isLoadingItemData,
    closeAddItemModal,
    itemSelected,
    selectedItems,
    searchTerm,
    handleUomChange,
    handleModalQuantityChange,
    handleCostPriceChange,
    confirmAddItems,
    canStoreAddItemsToPO,
    showAutosaveMessage,
    handlePOLineFocus,
    handleLeavePageModalConfirm,
    handleLeavePageModalClose,
    handleLeavePageModalCancel,
    blocker,
    modifiedPOLines,
    isTaxRateConfigEnabled,
    totalCostPriceIncludingTax,
    closeOverReceivingWarning,
    disableUomLac,
    isDownloading,
  };
};